import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { lighten } from 'polished';
import { Row, Col } from 'react-bootstrap';
import { Field, formValues } from 'redux-form';
import ToggleSwitch from 'components/atoms/form/ToggleSwitch';
import CurrentImportantSettingsContainer from 'containers/forms/entities/CurrentImportantSettingsContainer';
import { supportedChannelDisplayNamesKeyed } from 'helpers/channels';
import { NoPointerEventsDiv } from 'components/atoms/form/UtilityDivs';
import DeliveryPlatformConfig from 'components/organisms/entity/edit/DeliveryPlatformConfig';
import SwitchChannelToggleContainer from 'components/organisms/entity/edit/SwitchChannelToggleContainer';
import { PROGRAMMATIC_PLATFORMS } from 'constants/channels';

const Spacing = styled.div`
    margin: 10px;
`;

const ChannelWrapper = styled.div`
    overflow: show;
    background-color: #ffffff;
    border-bottom: 1px solid ${props => props.theme.colors.darkgray};
`;

const ExpandLink = styled.a`
    cursor: pointer;
    font-size: 15px;
    text-decoration: underline;
    margin-bottom: 5px;
    user-select: none;
`;

const FlexWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const Arrow = styled.span`
    display: inline-block;
    margin-right: 5px;
    transform: ${props => (!props.expanded ? null : 'rotate(90deg)')};
`;

const ToggleOpacity = styled.div`
    pointer-events: ${props => (props.enabled || !props.brandId ? null : 'none')};
    opacity: ${props => (props.enabled || !props.brandId ? null : '0.2')};
`;

const ToggleOpacityDecorated = formValues(props => ({ enabled: props.name, brandId: 'brandId' }))(ToggleOpacity);

const BorderRow = styled(Row)`
    border-top: 1px solid ${props => props.theme.colors.darkgray};
    display: flex;
    align-items: center;
    min-height: 52px;
    padding: 10px 0;
    transition: all .5s;
    margin: 0;

    :hover {
        background-color: ${props => lighten(0.59, props.theme.colors.primary)};

    }
`;

function WorkOrderChannelConfig(props) {
    const { adminPlatform } = props;
    const [expanded, setExpanded] = useState(props.defaultExpand || false);
    let channels = null;
    const isCampaignEngine = adminPlatform === 'salesforce';
    const isSiteDirect = channel => channel === 'site';

    if (expanded) {
        channels = Object.keys(supportedChannelDisplayNamesKeyed).map((channel) => {
            if (isSiteDirect(channel) && isCampaignEngine) {
                return (
                    <BorderRow key={channel}>
                        <Col xs={2}>
                            <NoPointerEventsDiv>
                                <Field name={`${props.fieldKey}.meta.channels.${channel}[0].enabled`} component={ToggleSwitch} scale="0.8" />
                            </NoPointerEventsDiv>
                        </Col>
                        <Col xs={4}>{supportedChannelDisplayNamesKeyed[channel]}</Col>
                        <Col xs={6}>
                        Site Direct is not fully compatible with Campaign Engine and is currently disabled.
                        </Col>
                    </BorderRow>
                );
            }

            return (
                <BorderRow key={channel}>
                    <Col xs={2}>
                        <ToggleOpacityDecorated name={`${props.fieldKey}.meta.overrideChannels`}>
                            <Field name={`${props.fieldKey}.meta.channels.${channel}[0].enabled`} component={ToggleSwitch} scale="0.8" />
                        </ToggleOpacityDecorated>
                    </Col>
                    <Col xs={4}>{supportedChannelDisplayNamesKeyed[channel]}</Col>
                    <Col xs={6}>
                        <DeliveryPlatformConfig
                            readOnly={false}
                            channel={channel}
                            deliveryPlatform={`${props.fieldKey}.meta.channels.${channel}[0].deliveryPlatform`}
                        />
                    </Col>
                </BorderRow>
            );
        });
    }

    return (
        <React.Fragment>
            <Spacing>
                <ExpandLink onClick={() => setExpanded(!expanded)}>
                    <Arrow expanded={expanded}>►</Arrow>
                    {expanded ? 'Hide' : 'Show'} Channel Config
                </ExpandLink>
            </Spacing>
            {expanded ?
                <React.Fragment>
                    <CurrentImportantSettingsContainer>
                        {({ selectedBrandId }) => (selectedBrandId ? (
                            <Row>
                                <Col xs={12}>
                                    <FlexWrapper>
                                        <Field name={`${props.fieldKey}.meta.overrideChannels`} component={ToggleSwitch} onChange={(event, newValue, prevValue) => {
                                            if (newValue === false && prevValue === true) {
                                                if (window.confirm('Are you sure you wish to disable the channel overrides for this work order? Doing so will override this workorder\'s enabled channel settings to match the available channels.')) { // eslint-disable-line
                                                    props.handleOverrideRemoval({ fieldKey: props.fieldKey });
                                                } else {
                                                    event.preventDefault();
                                                }
                                            }
                                        }} scale="0.8" /> Override Brand Configuration
                                    </FlexWrapper>
                                </Col>
                            </Row>
                        ) : null)}
                    </CurrentImportantSettingsContainer>
                    <Spacing>
                        <Row>
                            <Col xs={2}>
                                <ToggleOpacityDecorated name={`${props.fieldKey}.meta.overrideChannels`}>
                                    <strong>Enabled</strong>
                                </ToggleOpacityDecorated>
                            </Col>
                            <Col xs={4}><strong>Channel</strong></Col>
                            <Col xs={6}><strong>Delivery Platform</strong>
                                <SwitchChannelToggleContainer
                                    switchWorkOrderPlatforms={props.switchWorkOrderPlatforms}
                                    fieldKey={props.fieldKey}
                                    platforms={ Object.values(PROGRAMMATIC_PLATFORMS) }
                                />
                            </Col>
                        </Row>
                    </Spacing>
                    <ChannelWrapper>
                        {channels}
                    </ChannelWrapper>
                </React.Fragment>
                : null}
        </React.Fragment>
    );
}

WorkOrderChannelConfig.propTypes = {
    fieldKey: PropTypes.string,
    adminPlatform: PropTypes.string,
    handleOverrideRemoval: PropTypes.func,
    switchWorkOrderPlatforms: PropTypes.func,
};

export default WorkOrderChannelConfig;



// WEBPACK FOOTER //
// ./src/components/organisms/entity/edit/WorkOrderChannelConfig.js